import {
  Armchair,
  BarChart3,
  BookOpen,
  BrainCircuit,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Flame,
  RotateCcw,
  Timer,
} from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import { ContinueLearningCard } from "../../components/sc/ContinueLearningCard";
import { GlanceStatCard } from "../../components/sc/GlanceStatCard";
import { QuickActionCompact } from "../../components/sc/QuickActionCompact";
import { StudyCentreHero } from "../../components/sc/StudyCentreHero";
import { WeekActivityChart } from "../../components/sc/WeekActivityChart";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import { ziggySrc, type ZiggyAge } from "../../lib/ziggy";
import type { LearnHubAction } from "../../lib/learning/types";
import { useZiggyVariant } from "../../lib/ziggyContext";
import {
  useCoachSummary,
  useCourseNavigation,
  useHomeActivity,
  usePathways,
} from "../../lib/learning/hooks";
import { weekActivity, weekActivityTotal } from "../../lib/learning/weekActivity";
import { isReadForPleasurePlan } from "../../lib/learning/copy";
import { nextUpFromNavigation, progressPercentFromNavigation } from "../../lib/pathwayProgress";
import { isModuleDisabledError } from "../../lib/learning/errors";
import { intlLocale } from "../../lib/i18n/intl";
import {
  bookingWindowLabel,
  canBookSessions,
  dayLabel,
  slotLabel,
  useMySeat,
  type MySeat,
} from "../../lib/seat/api";

/**
 * Study-centre learner home — screenshot-aligned layout with real API data only.
 */
export function StudyCentreHome({
  firstName,
  ziggyAge = "grown",
  onOpenRevise,
  onOpenSeat,
}: {
  firstName: string;
  ziggyAge?: ZiggyAge;
  onOpenRevise: (action?: LearnHubAction) => void;
  onOpenSeat: () => void;
}) {
  const { strings, locale } = usePortalLocale();
  const { variant } = useZiggyVariant();

  const pathwaysQuery = usePathways();
  const plans = pathwaysQuery.data?.items ?? [];
  const activePlans = plans.filter((p) => p.status === "active" && p.onboarding_complete);
  const hasActivePlan = activePlans.length > 0;
  const primaryPlan = activePlans[0] ?? null;

  const summaryQuery = useCoachSummary(hasActivePlan);
  const summary = summaryQuery.data;
  const dueNow = summary?.due_now ?? 0;
  const reviewedToday = summary?.reviewed_today ?? 0;
  const cardsTotal = summary?.cards_total ?? 0;
  const studyStreak = summary?.study_streak_days ?? 0;
  const focusMinutes = summary?.focus_minutes_today ?? 0;
  const extraGlanceStats = (studyStreak > 0 ? 1 : 0) + (focusMinutes > 0 ? 1 : 0);

  const navQuery = useCourseNavigation(primaryPlan?.id ?? null, !!primaryPlan);
  const progressPercent = progressPercentFromNavigation(navQuery.data);
  const nextUp = nextUpFromNavigation(navQuery.data);

  const activityQuery = useHomeActivity(hasActivePlan);
  const activityDays = weekActivity(activityQuery.data?.items ?? [], new Date(), locale);
  const activityCount = weekActivityTotal(activityDays);

  const seatQuery = useMySeat();
  const seat = seatQuery.data ?? null;

  if (pathwaysQuery.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
        {strings.oneMoment}
      </div>
    );
  }

  if (pathwaysQuery.isError) {
    const moduleOff = isModuleDisabledError(pathwaysQuery.error);
    return (
      <div className="space-y-5 lg:space-y-6">
        <SectionCard className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="font-display text-lg font-semibold">
            {moduleOff ? strings.moduleDisabledHomeTitle : strings.homeLoadFailedTitle}
          </p>
          <p className="text-muted-foreground max-w-sm text-sm">
            {moduleOff ? strings.moduleDisabledHomeBody : strings.homeLoadFailedBody}
          </p>
          {!moduleOff && (
            <button
              type="button"
              onClick={() => pathwaysQuery.refetch()}
              className="bg-primary text-primary-foreground mt-1 inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
            >
              <RotateCcw className="size-4" /> {strings.homeRetry}
            </button>
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-5 lg:space-y-6">
      <StudyCentreHero
        firstName={firstName}
        mascotSrc={ziggySrc(variant, ziggyAge, "happy")}
        onGetStarted={onOpenRevise}
        showCta={!hasActivePlan}
      />

      {hasActivePlan && (
        <Reveal index={1}>
          <SectionHeader title={strings.todayAtAGlance} />
          {summaryQuery.isError ? (
            <SectionCard className="border-destructive/30 bg-destructive/5 space-y-3 p-5">
              <div role="alert" className="space-y-1">
                <p className="font-display font-semibold">{strings.studyStatsUnavailableTitle}</p>
                <p className="text-muted-foreground text-sm">{strings.studyStatsUnavailableBody}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void summaryQuery.refetch()}
              >
                <RotateCcw className="size-4" aria-hidden="true" />
                {strings.homeRetry}
              </Button>
            </SectionCard>
          ) : (
            <>
              {dueNow === 0 && reviewedToday > 0 && (
                <p className="text-muted-foreground -mt-1 mb-3 text-xs font-medium">
                  {strings.caughtUpToday}
                </p>
              )}
              <div
                className={cn(
                  "grid gap-3 sm:gap-4",
                  extraGlanceStats === 0 && "grid-cols-3",
                  extraGlanceStats === 1 && "grid-cols-2 sm:grid-cols-4",
                  extraGlanceStats === 2 && "grid-cols-2 sm:grid-cols-5",
                )}
              >
                <GlanceStatCard
                  label={strings.dueNow}
                  value={dueNow}
                  tint="teal"
                  loading={summaryQuery.isLoading}
                  icon={<ClipboardCheck className="size-5" aria-hidden="true" />}
                />
                <GlanceStatCard
                  label={strings.reviewedToday}
                  value={reviewedToday}
                  tint="violet"
                  loading={summaryQuery.isLoading}
                  icon={<CheckCircle2 className="size-5" aria-hidden="true" />}
                />
                <GlanceStatCard
                  label={strings.inRotation}
                  value={cardsTotal}
                  tint="amber"
                  loading={summaryQuery.isLoading}
                  icon={<RotateCcw className="size-5" aria-hidden="true" />}
                />
                {studyStreak > 0 && (
                  <GlanceStatCard
                    label={strings.studyStreakLabel}
                    value={studyStreak}
                    tint="violet"
                    loading={summaryQuery.isLoading}
                    icon={<Flame className="size-5" aria-hidden="true" />}
                  />
                )}
                {focusMinutes > 0 && (
                  <GlanceStatCard
                    label={strings.focusMinutesLabel}
                    value={focusMinutes}
                    tint="teal"
                    className={extraGlanceStats === 2 ? "col-span-2 sm:col-span-1" : undefined}
                    loading={summaryQuery.isLoading}
                    icon={<Timer className="size-5" aria-hidden="true" />}
                  />
                )}
              </div>
            </>
          )}
          <div className="mt-4">
            <WeekActivityChart
              title={strings.weekActivityTitle}
              days={activityDays}
              emptyLabel={strings.weekActivityEmpty}
              errorLabel={strings.weekActivityUnavailable}
              retryLabel={strings.homeRetry}
              truncatedLabel={strings.weekActivityLatestOnly}
              truncated={(activityQuery.data?.total ?? 0) > (activityQuery.data?.items.length ?? 0)}
              status={
                activityQuery.isLoading
                  ? "loading"
                  : activityQuery.isError
                    ? "error"
                    : activityCount === 0
                      ? "empty"
                      : "ready"
              }
              onRetry={() => void activityQuery.refetch()}
            />
          </div>
        </Reveal>
      )}

      <Reveal index={2}>
        <SectionHeader title={strings.yourSeat} />
        <button
          type="button"
          onClick={onOpenSeat}
          className="group w-full text-left transition-transform duration-150 ease-out active:scale-[0.99] motion-reduce:transition-none"
        >
          <SectionCard className="flex items-center gap-4 p-4 sm:p-5">
            <span
              className={cn(
                "grid size-12 shrink-0 place-items-center rounded-2xl",
                seat?.today || seat?.assigned
                  ? "bg-primary/12 text-primary"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <Armchair className="size-6" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display text-lg font-semibold leading-tight">
                  {seatHeadline(seatQuery.isLoading, seat, strings)}
                </p>
                {seat?.assigned && !seat?.today && (
                  <span className="bg-primary/12 text-primary rounded-full px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide">
                    {strings.regular}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {seatSubline(seatQuery.isLoading, seat, strings, locale)}
              </p>
            </div>
            <ChevronRight
              className="text-muted-foreground size-5 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
              aria-hidden="true"
            />
          </SectionCard>
        </button>
      </Reveal>

      <Reveal index={3}>
        <SectionHeader
          title={strings.continueLearning}
          action={strings.viewAll}
          onAction={onOpenRevise}
        />
        {primaryPlan ? (
          <ContinueLearningCard
            plan={primaryPlan}
            progressPercent={progressPercent}
            nextUp={nextUp}
            onContinue={() => {
              if (
                primaryPlan &&
                !isReadForPleasurePlan(primaryPlan.purpose) &&
                primaryPlan.roadmap_source
              ) {
                onOpenRevise("roadmap");
                return;
              }
              onOpenRevise();
            }}
            accent={primaryPlan.purpose === "casual_reading" ? "green" : "violet"}
          />
        ) : (
          <SectionCard className="space-y-3 p-5">
            <p className="font-display text-base font-semibold">{strings.setupFirstPlan}</p>
            <p className="text-muted-foreground text-sm">{strings.setupFirstPlanHint}</p>
          </SectionCard>
        )}
      </Reveal>

      <Reveal index={4}>
        <SectionHeader title={strings.quickActions} />
        <QuickActionCompact
          items={[
            {
              id: "quiz",
              label: strings.takeQuiz,
              icon: BrainCircuit,
              tint: "violet",
              onClick: () => onOpenRevise("quiz"),
            },
            {
              id: "practice",
              label: strings.practiceTest,
              icon: ClipboardCheck,
              tint: "orange",
              onClick: () => onOpenRevise("practice"),
            },
            {
              id: "reading",
              label: strings.logReading,
              icon: BookOpen,
              tint: "green",
              onClick: () => onOpenRevise("reading"),
            },
            {
              id: "progress",
              label: strings.progress,
              icon: BarChart3,
              tint: "blue",
              onClick: () => onOpenRevise("progress"),
            },
          ]}
        />
      </Reveal>
    </div>
  );
}

type HomeStrings = ReturnType<typeof usePortalLocale>["strings"];

function seatHeadline(loading: boolean, seat: MySeat | null, s: HomeStrings): string {
  if (loading) return s.checkingSeat;
  if (seat?.today) return seat.today.place?.seat_number ?? s.seatHeldToday;
  if (seat?.assigned) return seat.assigned.seat_number ?? s.seatAssigned;
  if (seat?.plan?.kind === "no_seat") return s.sitAnywhere;
  const next = seat?.upcoming[0];
  if (next) return next.place?.seat_number ?? s.bookedAhead;
  if (seat && canBookSessions(seat)) return s.noBookingYet;
  return s.noSeatYet;
}

function seatSubline(
  loading: boolean,
  seat: MySeat | null,
  s: HomeStrings,
  locale: "en" | "hi",
): string {
  if (loading) return s.oneMoment;
  if (seat?.today) {
    const zone = seat.today.place?.zone_name;
    const when = slotLabel(seat.today.slot_key, locale);
    const state = seat.today.status === "checked_in" ? s.checkedIn : s.notCheckedInYet;
    return [zone, when, state].filter(Boolean).join(" · ");
  }
  if (seat?.assigned) return s.dedicatedSeatNoToday;
  if (seat?.plan?.kind === "no_seat") return s.sitAnywhereHint;
  const next = seat?.upcoming[0];
  if (next) {
    return [
      s.nextBooking(dayLabel(next.date_key, intlLocale(locale))),
      bookingWindowLabel(next, locale),
    ].join(" · ");
  }
  if (seat && canBookSessions(seat)) return s.bookSeatHint;
  return s.seatAssignHint;
}
